import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";

/** One SGFI competition: a game, an age group and a gender, for a season. */
export interface SgfiEvent {
  id: number;
  year: string;
  gameId: number;
  gameName: string;
  ageRange: string;
  gender: string;
  lastDate: string;
  reportingDate: string;
  venue: string;
  description: string;
  feeOverride: string;
  students?: number;
}

@Injectable({ providedIn: "root" })
export class SgfiEventsService {
  private base = environment.baseUrl + "SgfiEntries";

  constructor(private http: HttpClient) {}

  list(year: string): Observable<SgfiEvent[]> {
    return this.http.get<SgfiEvent[]>(this.base + "/events?year=" + encodeURIComponent(year));
  }

  get(id: number): Observable<SgfiEvent> {
    return this.http.get<SgfiEvent>(this.base + "/events/" + id);
  }

  save(body: Partial<SgfiEvent> & { id?: number }) {
    return this.http.post<{ status: string; id: number }>(this.base + "/saveEvent", body);
  }

  remove(id: number) {
    return this.http.post<{ status: string }>(this.base + "/deleteEvent/" + id, {});
  }
}

/* Mirrors backend/src/common/utils/domain.ts sportImage(). The images are
   320px copies of the web app's set - the originals are 30MB all told, far too
   heavy to ship inside the admin bundle. */
const SPORT_IMAGE: { [key: string]: string } = {
  football: "football.jpg", cricket: "cricket.jpg", basketball: "basketball.jpg",
  volleyball: "volleyball.jpg", kabaddi: "kabaddi.jpg", kho: "kho-kho.jpg", "kho kho": "kho-kho.jpg",
  badminton: "badminton.jpg", "table tennis": "table-tennis.jpg", tennis: "tennis.jpg",
  swimming: "swimming.jpg", athletics: "athletics.jpg", chess: "chess.jpg", archery: "archery.jpg",
  judo: "judo.jpg", handball: "handball.jpg", squash: "squash.jpg", shooting: "shooting.jpg",
  taekwondo: "taekwondo.jpg", yogasana: "yogasana.jpg", yoga: "yogasana.jpg", fencing: "fencing.jpg",
  skating: "skating.jpg", karate: "karate.jpg", pickleball: "pickleball.jpg", cycling: "cycling.jpg",
};

/** Sport name to its card image: exact match, then substring, else default. */
export function sportImage(name?: string | null): string {
  const base = "assets/sports/";
  if (!name) return base + "default.jpg";
  // Legacy sport names carry qualifiers like "ATHLETICS (Jr.)".
  const key = name.toLowerCase().replace(/\s*\([^)]*\)\s*/g, " ").trim();
  if (SPORT_IMAGE[key]) return base + SPORT_IMAGE[key];
  for (const sport of Object.keys(SPORT_IMAGE)) {
    if (key.indexOf(sport) >= 0 || sport.indexOf(key) >= 0) return base + SPORT_IMAGE[sport];
  }
  return base + "default.jpg";
}

/** "2026-10-22" to "Thursday, 22nd October 2026". Blank stays blank. */
export function longDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d.getTime())) return iso;
  const day = d.getUTCDate();
  // 11th, 12th and 13th break the last-digit rule.
  const teen = day % 100 >= 11 && day % 100 <= 13;
  const suffix = teen ? "th" : ["th", "st", "nd", "rd"][day % 10] || "th";
  const weekday = d.toLocaleDateString("en-GB", { weekday: "long", timeZone: "UTC" });
  const month = d.toLocaleDateString("en-GB", { month: "long", timeZone: "UTC" });
  return weekday + ", " + day + suffix + " " + month + " " + d.getUTCFullYear();
}
